import React, { useState, useRef, useCallback } from 'react';
import { useShortcut, Scope, Key } from '@trello/keybindings';
import { forTemplate } from 'app/common/lib/util/i18n';
import cx from 'classnames';
import { SuggestedSearches } from './SuggestedSearches';
import { SearchResults } from './SearchResults';
import styles from './UnsplashSearch.less';
import Icon, { IconName } from 'app/common/components/controls/icon';
import { track } from '@trello/analytics';

const format = forTemplate('card_cover_chooser');

interface UnsplashSearchProps {
  cardId: string;
  onSetCover: () => void;
  onSetSearching: (searching: boolean) => void;
}

export const UnsplashSearch: React.FunctionComponent<UnsplashSearchProps> = ({
  cardId,
  onSetCover,
  onSetSearching,
}) => {
  const input = useRef<HTMLInputElement>(null);

  const [searchInputValue, setSearchInputValue] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [isHoveringOverClearButton, setIsHoveringOverClearButton] = useState(
    false,
  );

  const clearSearch = useCallback(
    () => {
      setSearchInputValue('');
      setIsActive(false);
      onSetSearching(false);
    },
    [onSetSearching],
  );

  useShortcut(
    () => {
      clearSearch();
      track('Card Cover Chooser', 'Clear Search', 'Via Escape Key');
    },
    {
      scope: Scope.Popover,
      key: Key.Escape,
      enabled: isActive,
    },
  );

  const onSuggestionSelectionCallback = useCallback(
    (searchText: string) => {
      setSearchInputValue(searchText);
      onSetSearching(true);

      if (input.current) {
        const searchTextLength = searchText.length;
        input.current.focus();
        input.current.setSelectionRange(searchTextLength, searchTextLength);
        track('Card Cover Chooser', 'Click Suggestion', searchText);
      }
    },
    [onSetSearching],
  );

  const hasValidSearchQuery = searchInputValue.trim().length > 0;
  const showSuggestedSearches = !hasValidSearchQuery;
  const showSearchResults = hasValidSearchQuery;

  return (
    <div className={styles.unsplashSearch}>
      <div className={styles.unsplashSearchInputSection}>
        <input
          type="text"
          autoFocus={true}
          onFocus={() => setIsActive(true)}
          onBlur={() => {
            if (!isHoveringOverClearButton) {
              setIsActive(false);
            }
          }}
          onChange={(e) => {
            const query = e.currentTarget.value;
            setSearchInputValue(query);
            onSetSearching(query.trim().length > 0);
          }}
          value={searchInputValue}
          placeholder={format('search-unsplash')}
          // NOTE: Popovers are rendered before being visible,
          // so autoFocus won't work as expected.  For now use
          // the old autofocus mechanism that Popover knows how
          // to handle for us.
          className={cx(styles.unsplashSearchInput, 'js-autofocus')}
          ref={input}
        />

        {isActive &&
          searchInputValue.length > 0 && (
            <button
              className={cx('button', styles.unsplashSearchClearButton)}
              onMouseEnter={() => setIsHoveringOverClearButton(true)}
              onMouseLeave={() => setIsHoveringOverClearButton(false)}
              onClick={() => {
                setIsHoveringOverClearButton(false);
                clearSearch();
                track('Card Cover Chooser', 'Clear Search', 'Via Button');
              }}
            >
              <Icon
                small
                name={IconName.Close}
                className={styles.unsplashSearchClearButtonIcon}
              />
            </button>
          )}
      </div>

      {showSuggestedSearches && (
        <SuggestedSearches onSelect={onSuggestionSelectionCallback} />
      )}

      {showSearchResults && (
        <SearchResults
          cardId={cardId}
          query={searchInputValue}
          onSetCover={onSetCover}
        />
      )}
    </div>
  );
};
